import React from 'react';
import {
    addDays,
    eachDayOfInterval,
    format,
    isSameDay,
    parseISO,
} from 'date-fns';
import {
    Stack,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { camelCaseToTitleCase } from '../../../../shared/utils';

interface SpaceItineraryProps {
    spaces: any[];
}

const SpaceItinerary = ({ spaces }: SpaceItineraryProps) => {
    type SpaceItem = {
        type: string;
        startDate: string;
        endDate: string;
        assignedName: string;
    };

    type DateGroup = {
        from: string;
        to: string;
        spaces: SpaceItem[];
    };

    const groupSpacesByPeriod = (spaces: SpaceItem[]): DateGroup[] => {
        const dateMap: Record<string, SpaceItem[]> = {};

        spaces.forEach((item) => {
            const days = eachDayOfInterval({
                start: parseISO(item.startDate),
                end: parseISO(item.endDate),
            });

            days.forEach((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                if (!dateMap[dateStr]) {
                    dateMap[dateStr] = [];
                }
                dateMap[dateStr].push(item);
            });
        });

        const sortedDates = Object.keys(dateMap).sort();

        const groups: DateGroup[] = [];
        let i = 0;
        while (i < sortedDates.length) {
            const currentDate = sortedDates[i];
            const currentSpaces = dateMap[currentDate];

            let j = i + 1;
            while (
                j < sortedDates.length &&
                JSON.stringify(dateMap[sortedDates[j]]) ===
                    JSON.stringify(currentSpaces) &&
                isSameDay(
                    parseISO(sortedDates[j]),
                    addDays(parseISO(sortedDates[j - 1]), 1),
                )
            ) {
                j++;
            }

            groups.push({
                from: sortedDates[i],
                to: sortedDates[j - 1],
                spaces: currentSpaces,
            });

            i = j;
        }

        return groups;
    };

    const commercialSpaces = groupSpacesByPeriod(spaces);

    return (
        <Stepper orientation='vertical' connector={null}>
            {commercialSpaces.map((data) => (
                <Step expanded>
                    <StepLabel
                        icon={
                            <CalendarMonth
                                fontSize='small'
                                sx={{
                                    ml: 0.5,
                                }}
                            />
                        }
                    >
                        <Typography
                            variant='overline'
                            sx={{
                                fontWeight: 'bold',
                            }}
                        >
                            {new Date(data.from).toDateString()}
                            {data.from !== data.to &&
                                ` – ${new Date(data.to).toDateString()}`}
                        </Typography>
                    </StepLabel>
                    <StepContent>
                        <Stack spacing={1}>
                            {data.spaces.map((space) => (
                                <div>
                                    <Typography
                                        variant='subtitle2'
                                        sx={{
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {camelCaseToTitleCase(space?.type)}
                                    </Typography>
                                    <Typography variant='body3'>
                                        {space.assignedName}
                                    </Typography>
                                </div>
                            ))}
                        </Stack>
                    </StepContent>
                </Step>
            ))}
            <Step />
        </Stepper>
    );
};

export default SpaceItinerary;
